<script setup lang="ts">
import ApplicationStepHeader from '@/components/common/application-step-header.vue';
import useApplicationCart from '@/composables/applicationCart/applicationCart';
import StfCard from '@/ui/card/StfCard.vue';
import useMobile from '@/composables/useMobileDependable';
import StfDivider from '@/ui/divider/StfDivider.vue';
import StfRow from '@/ui/columns/StfRow.vue';
import StfCol from '@/ui/columns/StfCol.vue';
import useCountry from '@/composables/countryDependable';
import {useCheckoutData} from '@/components/payment-view/data/checkout';
import BillingSection from '@/components/payment-view/billing-section.vue';
import StfButton from '@/ui/button/StfButton.vue';
import {computed, nextTick, watch} from 'vue';
import DesktopLayout from '@/components/payment-view/desktop-layout.vue';
import StfHeadingTwo from '@/ui/typography/StfHeadingTwo.vue';
import MobileLayout from '@/components/payment-view/mobile-layout.vue';
import useStripe from '@/components/payment-view/useStripe';
import ContactForm from '@/components/payment-view/contact-form.vue';
import {useContactStore} from '@/store/contact';
import usePaypal from '@/components/payment-view/usePaypal';
import StfBulletPoint from '@/ui/list/StfBulletPoint.vue';
import type Checkout from '@/core/model/checkout';
import { useSetUpPayment } from '@/composables/setUppayment';

const country = useCountry();

const { submitApplication, paymentRef } = useApplicationCart();
const { payWithStripe, loading: stripeLoading, error: stripeError } = useStripe(country);
const { loading: paypalLoading, reloadPaypal } = usePaypal();
const {checkout} = useCheckoutData();
const {isContactFormValid} = useContactStore();
const {isMobile} = useMobile()
const {setupLoading, applicationError, setup} = useSetUpPayment();

const layout = computed(() => {
    return isMobile.value ? MobileLayout : DesktopLayout;
});

const paymentLoading = computed(() => {
    return paypalLoading.value || stripeLoading.value || setupLoading.value;
});

const paymentError = computed(() => {
    if (stripeError.value) {
        return [stripeError.value];
    }

    return applicationError.value;
});

const setUpPayment = async () => {
    await setup();
}

const onClickPay = async () => {
    if (!isContactFormValid()) {
        return;
    }
    applicationError.value = [];
    const response = await submitApplication();

    if (!response.success) {
        applicationError.value = response.data;
        return;
    }

    await payWithStripe(paymentRef.value);
}

watch(layout, () => {
    nextTick(() => {
        reloadPaypal();
    });
});

setUpPayment();

</script>

<template>
    <StfCard :stickBottomDivider="!isMobile">
        <template #title>
            <ApplicationStepHeader title="Payment" />
            <StfAlert v-if="paymentError.length > 0" type="error" class="my-2">
                <template #icon>
                    <StfIcon icon="lightbulb" size="small" color="black" />
                </template>
                {{ paymentError[0] }}
                <StfBulletPoint v-if="paymentError.length > 1">
                    <template v-for="(item, index) in paymentError" >
                        <li v-if="index !== 0" :key="index" v-html="item"></li>
                    </template>
                </StfBulletPoint>
            </StfAlert>
        </template>
        <template #content>
            <component :is="layout">
                <template #left>
                    <StfRow>
                        <StfCol col="12">
                            <contact-form></contact-form>
                        </StfCol>
                        <StfCol col="12">
                            <div class="my-1"><StfIcon icon="lock" size="small" class="mr-1"/>Secure and encrypted payment</div>
                            <StfButton size="large" @click="onClickPay" :loading="paymentLoading">
                                <StfIcon icon="lock"/>
                                CHECKOUT NOW
                            </StfButton>
                        </StfCol>
                        <StfCol col="12">
                            <div class="mt-3 has-text-centered is-clickable" style="width: 100%;" @click="onClickPay">
                                <img :src="`/img/${country.paymentMethodsImageName}`">
                            </div>
                        </StfCol>
                        <StfCol col="12">
                            <StfDivider text="OR" />
                        </StfCol>
                        <StfCol col="12">
                            <div id="paypal-button-container"></div>
                        </StfCol>
                    </StfRow>
                </template>
                <template #right>
                    <StfRow>
                        <StfCol col="12">
                            <StfHeadingTwo>Payment Summary</StfHeadingTwo>
                        </StfCol>
                        <StfCol col="12">
                            <billing-section :checkout="checkout as Checkout" :loading="setupLoading" />
                        </StfCol>
                    </StfRow>
                </template>
            </component>
        </template>
    </StfCard>
</template>
